import { useState, useEffect, useRef } from "react";
import { getDayPhase, PHASE_LABELS } from "@/components/OceanWeather";
import { Sun, Moon, Sunrise, Sunset, Volume2, VolumeX } from "lucide-react";

const PHASE_ICONS = { dawn: Sunrise, day: Sun, sunset: Sunset, night: Moon };

export const SCENE_BY_TYPE = {
  island_base: "/islands/scene_standard.jpg",
  island_premium_villa: "/islands/scene_premium.jpg",
  island_ultra_premium: "/islands/scene_ultra.jpg",
};

export const getSceneImage = (islandType, isPremium) =>
  SCENE_BY_TYPE[islandType] || (isPremium ? "/islands/scene_premium.jpg" : "/islands/scene_standard.jpg");

const GRADE = {
  dawn: [
    { background: "linear-gradient(180deg, rgba(255,170,115,0.5) 0%, rgba(255,125,150,0.25) 40%, transparent 72%)", mixBlendMode: "soft-light" },
    { background: "linear-gradient(180deg, rgba(70,50,110,0.22) 0%, transparent 55%)", mixBlendMode: "multiply" },
  ],
  day: [
    { background: "radial-gradient(120% 90% at 50% 40%, transparent 55%, rgba(4,20,40,0.35) 100%)" },
  ],
  sunset: [
    { background: "linear-gradient(180deg, rgba(255,105,45,0.62) 0%, rgba(255,55,110,0.34) 48%, rgba(80,30,110,0.28) 100%)", mixBlendMode: "soft-light" },
    { background: "linear-gradient(180deg, rgba(140,55,20,0.3) 0%, rgba(60,25,80,0.28) 100%)", mixBlendMode: "multiply" },
  ],
  night: [
    { background: "linear-gradient(180deg, rgba(8,14,42,0.82) 0%, rgba(10,18,52,0.68) 50%, rgba(5,10,34,0.78) 100%)", mixBlendMode: "multiply" },
    { background: "linear-gradient(180deg, rgba(20,35,90,0.3) 0%, transparent 60%)", mixBlendMode: "screen" },
  ],
};

/* Realistic surf sound via WebAudio (brown noise + lowpass + slow swell LFO) */
const useOceanSound = (enabled) => {
  const nodesRef = useRef(null);
  useEffect(() => {
    if (!enabled) {
      if (nodesRef.current) { try { clearInterval(nodesRef.current.gullTimer); nodesRef.current.ctx.close(); } catch {} nodesRef.current = null; }
      return;
    }
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const size = ctx.sampleRate * 4;
      const buffer = ctx.createBuffer(1, size, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      let last = 0;
      for (let i = 0; i < size; i++) { const w = Math.random() * 2 - 1; last = (last + 0.02 * w) / 1.02; data[i] = last * 3.5; }
      const src = ctx.createBufferSource(); src.buffer = buffer; src.loop = true;
      const filter = ctx.createBiquadFilter(); filter.type = "lowpass"; filter.frequency.value = 750; filter.Q.value = 0.6;
      const gain = ctx.createGain(); gain.gain.value = 0.07;
      const lfo = ctx.createOscillator(); lfo.frequency.value = 0.09;
      const lfoG = ctx.createGain(); lfoG.gain.value = 0.045;
      lfo.connect(lfoG); lfoG.connect(gain.gain);
      const lfo2 = ctx.createOscillator(); lfo2.frequency.value = 0.23;
      const lfo2G = ctx.createGain(); lfo2G.gain.value = 0.02;
      lfo2.connect(lfo2G); lfo2G.connect(gain.gain);
      src.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
      src.start(); lfo.start(); lfo2.start();
      // light breeze (filtered noise, slow swell)
      const bsize = ctx.sampleRate * 2;
      const bbuf = ctx.createBuffer(1, bsize, ctx.sampleRate);
      const bd = bbuf.getChannelData(0);
      for (let i = 0; i < bsize; i++) bd[i] = Math.random() * 2 - 1;
      const bsrc = ctx.createBufferSource(); bsrc.buffer = bbuf; bsrc.loop = true;
      const bhp = ctx.createBiquadFilter(); bhp.type = "highpass"; bhp.frequency.value = 900;
      const blp = ctx.createBiquadFilter(); blp.type = "lowpass"; blp.frequency.value = 3200;
      const bg = ctx.createGain(); bg.gain.value = 0.011;
      const blfo = ctx.createOscillator(); blfo.frequency.value = 0.05;
      const blfoG = ctx.createGain(); blfoG.gain.value = 0.007;
      blfo.connect(blfoG); blfoG.connect(bg.gain);
      bsrc.connect(bhp); bhp.connect(blp); blp.connect(bg); bg.connect(ctx.destination);
      bsrc.start(); blfo.start();
      // distant seagulls (synthesized cries, random bursts)
      const gullMaster = ctx.createGain(); gullMaster.gain.value = 0.5;
      const gullLp = ctx.createBiquadFilter(); gullLp.type = "lowpass"; gullLp.frequency.value = 3500;
      gullMaster.connect(gullLp); gullLp.connect(ctx.destination);
      const cry = (t0, base) => {
        const osc = ctx.createOscillator(); osc.type = "sawtooth";
        const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = base * 2; bp.Q.value = 1.5;
        const g = ctx.createGain();
        osc.connect(bp); bp.connect(g); g.connect(gullMaster);
        osc.frequency.setValueAtTime(base, t0);
        osc.frequency.exponentialRampToValueAtTime(base * 1.35, t0 + 0.09);
        osc.frequency.exponentialRampToValueAtTime(base * 0.75, t0 + 0.38);
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(0.026, t0 + 0.06);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.42);
        osc.start(t0); osc.stop(t0 + 0.5);
      };
      const gullBurst = () => {
        const n = 1 + Math.floor(Math.random() * 3);
        const base = 950 + Math.random() * 350;
        for (let i = 0; i < n; i++) cry(ctx.currentTime + 0.1 + i * (0.38 + Math.random() * 0.2), base * (0.95 + Math.random() * 0.1));
      };
      const gullTimer = setInterval(() => { if (Math.random() < 0.75) gullBurst(); }, 9000);
      setTimeout(gullBurst, 2500);
      nodesRef.current = { ctx, gullTimer };
    } catch {}
    return () => { if (nodesRef.current) { try { clearInterval(nodesRef.current.gullTimer); nodesRef.current.ctx.close(); } catch {} nodesRef.current = null; } };
  }, [enabled]);
};

const Stars = () => (
  <div className="absolute inset-0 pointer-events-none" style={{ height: "55%" }}>
    {Array.from({ length: 46 }).map((_, i) => {
      const left = (i * 41) % 100;
      const top = (i * 23) % 100;
      const size = i % 5 === 0 ? 2.5 : 1.5;
      return (
        <span key={i} style={{
          position: "absolute", left: `${left}%`, top: `${top}%`, width: size, height: size,
          borderRadius: "50%", background: "#eef2ff",
          boxShadow: "0 0 4px 1px rgba(220,230,255,0.7)",
          animation: `isl-twinkle ${2.4 + (i % 5) * 0.8}s ease-in-out ${(i % 7) * 0.5}s infinite`,
        }} />
      );
    })}
  </div>
);

const Waves = ({ phase }) => {
  const foam = phase === "night" ? "rgba(200,220,255,0.16)" : "rgba(255,255,255,0.3)";
  const foam2 = phase === "night" ? "rgba(180,205,250,0.1)" : "rgba(255,255,255,0.18)";
  const path = "M0,60 C120,30 240,90 360,60 C480,30 600,90 720,60 C840,30 960,90 1080,60 C1200,30 1320,90 1440,60 L1440,120 L0,120 Z";
  return (
    <div className="absolute inset-x-0 bottom-0 pointer-events-none" style={{ height: "26%" }}>
      <div style={{ position: "absolute", bottom: "34%", left: 0, width: "200%", height: "50%", animation: "isl-wave 16s linear infinite" }}>
        <svg viewBox="0 0 1440 120" preserveAspectRatio="none" style={{ width: "50%", height: "100%", float: "left" }}><path d={path} fill={foam2} /></svg>
        <svg viewBox="0 0 1440 120" preserveAspectRatio="none" style={{ width: "50%", height: "100%", float: "left" }}><path d={path} fill={foam2} /></svg>
      </div>
      <div style={{ position: "absolute", bottom: 0, left: 0, width: "200%", height: "62%", animation: "isl-wave 9s linear infinite reverse" }}>
        <svg viewBox="0 0 1440 120" preserveAspectRatio="none" style={{ width: "50%", height: "100%", float: "left" }}><path d={path} fill={foam} /></svg>
        <svg viewBox="0 0 1440 120" preserveAspectRatio="none" style={{ width: "50%", height: "100%", float: "left" }}><path d={path} fill={foam} /></svg>
      </div>
    </div>
  );
};

export function IslandScene({ isPremium, islandType }) {
  const phase = getDayPhase();
  const PhaseIcon = PHASE_ICONS[phase];
  const scene = getSceneImage(islandType, isPremium);
  const cloudOpacity = phase === "night" ? 0.2 : phase === "day" ? 0.55 : 0.4;
  const [soundOn, setSoundOn] = useState(true);
  useOceanSound(soundOn);

  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="island-scene" data-phase={phase} style={{ background: "#04101f" }}>
      <style>{`
        @keyframes isl-kb { from { transform: scale(1.04) translateY(0); } to { transform: scale(1.14) translateY(-1.5%); } }
        @keyframes isl-drift { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes isl-wave { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes isl-twinkle { 0%,100% { opacity: 0.25; } 50% { opacity: 1; } }
        @keyframes isl-shimmer { 0%,100% { opacity: 0.1; transform: translateX(-6%); } 50% { opacity: 0.32; transform: translateX(6%); } }
        @keyframes isl-glow { 0%,100% { opacity: 0.55; } 50% { opacity: 0.9; } }
      `}</style>

      {/* Photographic scene with slow Ken Burns */}
      <img src={scene} alt="" draggable={false} className="absolute inset-0 w-full h-full object-cover"
           style={{ animation: "isl-kb 45s ease-in-out infinite alternate" }} />

      {/* Sun / moon light source */}
      {phase === "day" && (
        <div style={{ position: "absolute", top: "6%", right: "10%", width: 240, height: 240, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255,255,235,0.75) 0%, rgba(255,250,210,0.25) 40%, transparent 68%)",
          mixBlendMode: "screen", animation: "isl-glow 8s ease-in-out infinite" }} />
      )}
      {(phase === "sunset" || phase === "dawn") && (
        <div style={{ position: "absolute", top: "26%", left: phase === "dawn" ? "12%" : "auto", right: phase === "sunset" ? "10%" : "auto",
          width: 300, height: 300, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255,170,80,0.85) 0%, rgba(255,110,70,0.35) 42%, transparent 68%)",
          mixBlendMode: "screen", animation: "isl-glow 9s ease-in-out infinite" }} />
      )}
      {phase === "night" && (
        <>
          <Stars />
          <div style={{ position: "absolute", top: "8%", right: "14%", width: 58, height: 58, borderRadius: "50%",
            background: "radial-gradient(circle at 36% 34%, #f6f8fd 0%, #d3dcee 55%, #9fadcd 100%)",
            boxShadow: "0 0 55px 20px rgba(215,228,255,0.3)" }} />
          <div style={{ position: "absolute", bottom: "8%", right: "8%", width: "26%", height: "34%",
            background: "linear-gradient(180deg, rgba(205,222,255,0.32) 0%, rgba(205,222,255,0.05) 100%)",
            filter: "blur(6px)", mixBlendMode: "screen", animation: "isl-shimmer 7s ease-in-out infinite" }} />
        </>
      )}

      {/* Drifting realistic clouds (screen blend) */}
      <div style={{ position: "absolute", top: "-4%", left: 0, width: "200%", height: "34%",
        backgroundImage: "url(/islands/clouds.jpg)", backgroundRepeat: "repeat-x", backgroundSize: "auto 100%",
        mixBlendMode: "screen", opacity: cloudOpacity, filter: "blur(1px)", animation: "isl-drift 130s linear infinite" }} />
      <div style={{ position: "absolute", top: "14%", left: 0, width: "200%", height: "26%",
        backgroundImage: "url(/islands/clouds.jpg)", backgroundRepeat: "repeat-x", backgroundSize: "auto 100%",
        mixBlendMode: "screen", opacity: cloudOpacity * 0.5, filter: "blur(2px)", animation: "isl-drift 200s linear infinite reverse" }} />

      {/* Sunlight shimmer on the water (day/dawn/sunset) */}
      {phase !== "night" && (
        <div style={{ position: "absolute", bottom: "6%", left: "18%", width: "64%", height: "30%",
          background: `linear-gradient(180deg, ${phase === "day" ? "rgba(255,255,240,0.3)" : "rgba(255,190,120,0.35)"} 0%, transparent 100%)`,
          filter: "blur(8px)", mixBlendMode: "screen", animation: "isl-shimmer 6s ease-in-out infinite" }} />
      )}

      {/* Animated foam waves in the foreground */}
      <Waves phase={phase} />

      {/* Time-of-day color grading */}
      {GRADE[phase].map((s, i) => (
        <div key={i} style={{ position: "absolute", inset: 0, pointerEvents: "none", ...s }} />
      ))}

      {/* Phase chip + sound toggle */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <button onClick={() => setSoundOn(v => !v)}
          className="flex items-center justify-center w-8 h-8 rounded-full active:scale-90 transition-transform"
          style={{ background: "rgba(8,12,24,0.55)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.14)" }}
          data-testid="island-sound-toggle">
          {soundOn ? <Volume2 className="w-4 h-4 text-white/85" /> : <VolumeX className="w-4 h-4 text-white/45" />}
        </button>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
             style={{ background: "rgba(8,12,24,0.55)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.14)" }}
             data-testid="island-scene-phase">
          <PhaseIcon className="w-3.5 h-3.5 text-amber-200" />
          <span className="text-[11px] font-semibold text-white/85 tracking-wide">{PHASE_LABELS[phase]}</span>
        </div>
      </div>
    </div>
  );
}

export default IslandScene;
